import { forwardRef, useImperativeHandle, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { ShopsModel } from '../../models/Shops';
import { useSettingsManager } from '../../hooks';
import { AddPaymentMethodButton } from '../interaction';

export interface PaymentMethodsMobileModalRef {
  open: (shop: ShopsModel, onUpdate?: (methods: any[]) => void) => void;
}

export const PaymentMethodsMobileModal = forwardRef<PaymentMethodsMobileModalRef>((_, ref) => {
  const { t } = useTranslation('shopsModule');
  const modalRef = useRef<ModalCenterRef>(null);
  const [shop, setShop] = useState<ShopsModel | null>(null);

  // Hook para manejar métodos de pago
  const {
    linkedPaymentMethods,
    addPaymentMethod,
    removePaymentMethod,
    initializeFromShop,
    getPaymentMethodsByCategory,
  } = useSettingsManager();

  // Estado para el callback de actualización
  const [onUpdateCallback, setOnUpdateCallback] = useState<((methods: any[]) => void) | null>(null);

  // Detectar cambios en linkedPaymentMethods y notificar al padre
  useEffect(() => {
    if (onUpdateCallback && linkedPaymentMethods.length > 0) {
      onUpdateCallback(linkedPaymentMethods);
    }
  }, [linkedPaymentMethods, onUpdateCallback]);

  useImperativeHandle(ref, () => ({
    open: (selectedShop, onUpdate) => {
      setShop(selectedShop);
      initializeFromShop(selectedShop);
      setOnUpdateCallback(() => onUpdate || null);

      // Pequeño retraso para asegurar que el modal esté montado
      setTimeout(() => {
        if (modalRef.current) {
          modalRef.current.openModal();
        }
      }, 0);
    },
  }));

  if (!shop) return null;

  return (
    <ModalCenter
      ref={modalRef}
      title={t('modals.paymentMethods.title')}
      type="default"
      width="lg"
      className="bg-body-bg !outline-0"
      fullScreenMobile={true}
    >
      <div className="flex flex-col gap-6 h-full pt-4">
        {/* Texto explicativo */}
        <div>
          <p className="text-xs mb-3">{t('modals.settings.payments.linkPaymentMethods')}</p>
          <p className="text-xs">{t('modals.settings.payments.linkPaymentMethodsTooltip')}</p>
        </div>

        {/* Categorías de métodos de pago */}
        {(() => {
          const categories = getPaymentMethodsByCategory();

          return (
            <div className="space-y-7">
              {/* Pedido pagado */}
              <section>
                <h3 className="text-sm font-medium mb-3">{t('modals.settings.payments.categories.paidOrder')}</h3>
                <div className="flex flex-wrap gap-2">
                  <AddPaymentMethodButton
                    linkedPaymentMethods={categories.paidOrder}
                    paymentMethodId={1}
                    onAddMethod={addPaymentMethod}
                    onRemoveMethod={removePaymentMethod}
                  />
                </div>
              </section>

              {/* Transferencia bancaria */}
              <section>
                <h3 className="text-sm font-medium mb-3">{t('modals.settings.payments.categories.bankTransfer')}</h3>
                <div className="flex flex-wrap gap-2">
                  <AddPaymentMethodButton
                    linkedPaymentMethods={categories.bankTransfer}
                    paymentMethodId={2}
                    onAddMethod={addPaymentMethod}
                    onRemoveMethod={removePaymentMethod}
                  />
                </div>
              </section>

              {/* Contra reembolso */}
              <section>
                <h3 className="text-sm font-medium mb-3">{t('modals.settings.payments.categories.cashOnDelivery')}</h3>
                <div className="flex flex-wrap gap-2">
                  <AddPaymentMethodButton
                    linkedPaymentMethods={categories.cashOnDelivery}
                    paymentMethodId={3}
                    onAddMethod={addPaymentMethod}
                    onRemoveMethod={removePaymentMethod}
                  />
                </div>
              </section>
            </div>
          );
        })()}
      </div>
    </ModalCenter>
  );
});

PaymentMethodsMobileModal.displayName = 'PaymentMethodsMobileModal';
